import React, { useMemo } from 'react';
import { Quote } from '../../types';
import {
  formatQty,
  OrderSelection,
  orderPickerGroups,
  parseOrderQty,
} from '../../services/quoteOrderTranches';

/**
 * Positionsauswahl einer Teilbeauftragung: je offene Angebotsposition ein
 * Haken und die Menge (Vorbelegung = volle offene Menge), je Abschnitt ein
 * Haken für alles darin. Rechnen tut der Aufrufer über quoteOrderTranches —
 * hier wird nur angezeigt und der Auswahlzustand gepflegt.
 */
interface Props {
  quote: Quote;
  selection: OrderSelection;
  onChange: (next: OrderSelection) => void;
  disabled?: boolean;
}

const cellInput =
  'w-24 px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-right outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 disabled:bg-slate-50';

export const OrderLinesPicker: React.FC<Props> = ({ quote, selection, onChange, disabled }) => {
  const groups = useMemo(() => orderPickerGroups(quote), [quote]);

  const setRow = (id: string, patch: Partial<{ on: boolean; text: string }>) => {
    const cur = selection[id] ?? { on: false, text: '' };
    onChange({ ...selection, [id]: { ...cur, ...patch } });
  };

  const setGroup = (ids: string[], on: boolean) => {
    const next = { ...selection };
    for (const id of ids) next[id] = { ...(next[id] ?? { on: false, text: '' }), on };
    onChange(next);
  };

  if (groups.length === 0) {
    return <p className="text-sm text-slate-500">Keine offenen Positionen.</p>;
  }

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
      {groups.map((g) => {
        const ids = g.rows.map((r) => r.item.id);
        const onCount = ids.filter((id) => selection[id]?.on).length;
        const all = onCount === ids.length;
        return (
          <div key={g.id || 'ohne'}>
            <label className="flex items-center gap-3 px-4 py-2 bg-slate-50 cursor-pointer">
              <input
                type="checkbox"
                checked={all}
                ref={(el) => { if (el) el.indeterminate = onCount > 0 && !all; }}
                onChange={() => setGroup(ids, !all)}
                disabled={disabled}
              />
              <span className="text-xs font-bold uppercase tracking-widest text-slate-600">{g.title}</span>
            </label>
            {g.rows.map(({ item, state }) => {
              const row = selection[item.id] ?? { on: false, text: '' };
              const qty = parseOrderQty(row.text);
              const tooMuch = row.on && qty !== null && qty - state.open > 1e-6;
              const invalid = row.on && (qty === null || qty <= 0);
              return (
                <div key={item.id} className="flex items-center gap-3 px-4 py-2">
                  <input
                    type="checkbox"
                    checked={row.on}
                    onChange={() => setRow(item.id, { on: !row.on })}
                    disabled={disabled}
                    aria-label={`${item.title} beauftragen`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-700 truncate">
                      <span className="font-mono text-xs text-slate-400 mr-2">{item.posNumber || item.lvPosition}</span>
                      {item.title}
                    </div>
                    <div className="text-[11px] text-slate-400">
                      offen {formatQty(state.open)} {item.unit}
                      {state.ordered > 0 ? ` · beauftragt ${formatQty(state.ordered)}` : ''}
                      {state.declined > 0 ? ` · entfallen ${formatQty(state.declined)}` : ''}
                    </div>
                    {(tooMuch || invalid) && (
                      <div className="text-[11px] font-semibold text-red-600">
                        {tooMuch ? `Höchstens ${formatQty(state.open)} offen.` : 'Bitte eine Menge größer 0 eingeben.'}
                      </div>
                    )}
                  </div>
                  {/* NICHT type="number": ein Zahlenfeld leert e.target.value bei
                      Zwischenständen wie "2," — dieselbe Falle wie beim
                      Auftragswert im Annahmedialog. */}
                  <input
                    type="text"
                    inputMode="decimal"
                    className={`${cellInput} ${tooMuch || invalid ? 'border-red-300' : ''}`}
                    value={row.text}
                    onChange={(e) => setRow(item.id, { text: e.target.value, on: true })}
                    disabled={disabled || !row.on}
                  />
                  <span className="w-12 text-xs text-slate-500 truncate">{item.unit}</span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
